package com.devsusana.portfolio.pages.posts.es

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.blog.*
import com.varabyte.kobweb.core.Page

@Page("/posts/es/hybrid-pattern-mobile-ai")
@Composable
fun HybridPatternMobileAiPageEs() {
    ArticleScaffold(
        title = "El Patrón Híbrido en IA Móvil: Fast-Path Determinista (<5ms) vs LLM",
        date = "2026-09-15",
        readingTime = "7 min de lectura",
        tags = listOf("Clean Architecture", "Kotlin", "Performance", "Edge AI", "Jetpack Compose"),
        isSpanish = true
    ) {
        ArticleSectionHeader("1. El Antipatrón en IA Móvil: Arquitectura \"Solo LLM\"")

        ArticleParagraph(
            "Un error muy extendido al incorporar inteligencia artificial en productos móviles consiste en canalizar cualquier locución del usuario directamente hacia el Modelo de Lenguaje. Aunque parece sencillo sobre el papel, en producción desencadena deficiencias críticas:"
        )

        BulletItem("Latencia Inadmisible (6–12 segundos):", "La inferencia neuronal en silicio local demanda numerosos ciclos de procesamiento. Para operaciones directas (consultar el saldo de un alumno o listar clases canceladas), esperar varios segundos destruye por completo la usabilidad.")
        BulletItem("Consumo Térmico y de Batería:", "El cálculo continuo sobre la NPU o GPU calienta el terminal y merma rápidamente la autonomía de la batería.")
        BulletItem("Alucinaciones Aritméticas y Fácticas:", "Incluso los Modelos de Lenguaje Pequeños (1B–2B parámetros) ajustados con fine-tuning cometen errores notables en aritmética de fechas, conciliación de calendarios y cálculos contables.")

        ArticleSectionHeader("2. La Solución: Una Arquitectura Híbrida en Dos Niveles")

        ArticleParagraph(
            "En HomeTutorPro, nuestra asistente integrada SUE (Smart User Entity) implementa un Patrón Híbrido en Dos Niveles:"
        )

        CodeSnippet(
            code = """           ┌────────────────────────────────────────┐
           │        Intención del Usuario           │
           └───────────────────┬────────────────────┘
                               │
                ┌──────────────┴──────────────┐
                ▼                             ▼
    [NIVEL 1: FAST-PATH]           [NIVEL 2: LLM GENERATIVO]
    • Kotlin Puro / Room SQL       • RAG Local + MediaPipe
    • Saldos y agenda estructurada • Consultas pedagógicas complejas
    • Ejecución en < 5 ms          • Síntesis abierta y relacional
    • 0% de uso de GPU             • Inferencia acelerada por GPU
    • 100% precisión (0 alucinac.) • Ventana de 2048 tokens""",
            language = "Arquitectura Híbrida"
        )

        ArticleSectionHeader("3. Nivel 1: Fast-Path Determinista en Kotlin")

        ArticleParagraph(
            "Para consultas operativas y transaccionales, SueAgent elude el modelo generativo y resuelve la petición mediante algoritmos optimizados en Kotlin:"
        )

        BulletItem("Motor de Tiempo en Lenguaje Natural:", "Procesa horas exactas (17:00, 21:30) y expresiones coloquiales habladas (a las cinco, y cuarto, menos diez, medianoche).")
        BulletItem("Inferencia Contextual de Tarde:", "Si el profesor solicita una cita sin especificar franja horaria (\"ponme una clase a las 5\"), el motor infiere automáticamente el horario de tarde (17:00), adaptándose a la realidad operativa de las clases extraescolares.")
        BulletItem("Prevención Estricta de Solapamientos:", "Al reprogramar o agendar clases adicionales, SaveScheduleExceptionUseCase verifica solapamientos con clases existentes, excepciones previas y márgenes de desplazamiento. Si se produce un conflicto, la modificación se bloquea y se notifica al usuario.")

        CodeSnippet(
            code = """// Ejemplo: Evaluación determinista de colisiones en la capa de dominio
class SaveScheduleExceptionUseCase @Inject constructor(
    private val repository: ScheduleExceptionRepository,
    private val studentRepository: StudentRepository
) : ISaveScheduleExceptionUseCase {

    override suspend operator fun invoke(
        professorId: String,
        studentId: String,
        exception: ScheduleException
    ): Result<Unit, DomainError> {
        val allSchedules = studentRepository.getAllSchedules(professorId).first()
        
        // Comprobación determinista de colisiones en < 5 ms
        for (existing in allSchedules) {
            if (isTimeOverlap(exception.newStartTime, exception.newEndTime, existing.startTime, existing.endTime)) {
                val studentName = studentRepository.getStudentById(existing.studentId)?.name ?: "Alumno"
                return Result.Error(
                    DomainError.ConflictingStudent(
                        studentName = studentName,
                        time = "${'$'}{existing.startTime} - ${'$'}{existing.endTime}"
                    )
                )
            }
        }
        repository.saveException(professorId, studentId, exception)
        return Result.Success(Unit)
    }
}""",
            language = "Kotlin"
        )

        ArticleSectionHeader("4. Seguridad por Voz: Tarjetas de Confirmación en Dos Pasos")

        ArticleParagraph(
            "Las modificaciones destructivas en la base de datos (cancelar una clase agendada, registrar cobros, borrar expedientes) nunca deben ejecutarse bajo ambigüedad acústica."
        )

        BulletItem("1. Transición de Intención:", "SueAgent detecta la acción destructiva y sitúa SueUiState en el estado Prepare.")
        BulletItem("2. Muestra de Tarjeta Visual:", "Una tarjeta interactiva en Jetpack Compose (SuePendingAction) muestra los parámetros exactos de la modificación.")
        BulletItem("3. Locución de Confirmación:", "SUE formula una pregunta acústica directa (\"¿Confirmas cancelar la clase de Lucía el viernes? Di sí o no\").")
        BulletItem("4. Ejecución Transaccional:", "Únicamente tras la afirmación explícita por voz (\"Sí\" / \"Confirmar\") o pulsación en pantalla se ejecuta el caso de uso transaccional.")

        ArticleSectionHeader("5. Nivel 2: Inferencia Generativa con RAG Local")

        ArticleParagraph(
            "Solo cuando la consulta requiere síntesis abierta, asesoramiento contextual o análisis pedagógico complejo, la ejecución se delega en Gemma mediante MediaPipe GenAI:"
        )

        BulletItem("Planificación Pedagógica:", "\"¿Cómo me recomiendas organizar el repaso de física de Carlos antes de su examen?\"")
        BulletItem("Resumen de Recursos Compartidos:", "\"¿Qué materiales he compartido con cada alumno esta semana y qué huecos me quedan libres?\"")

        ArticleParagraph(
            "Al alimentar a Gemma con datos estructurados procedentes del Nivel 1, el modelo actúa puramente como un sintetizador elocuente, erradicando por completo las alucinaciones factuales."
        )

        ArticleSectionHeader("6. Conclusiones Clave de Arquitectura")

        BulletItem("Reserva los LLMs para el Lenguaje, no para la Lógica:", "Utiliza Kotlin, índices en Room y casos de uso de dominio para contabilidad, agenda y validaciones.")
        BulletItem("La Rapidez es la Característica Nº1:", "El 90% de las consultas en apps móviles de productividad son transaccionales. Resolverlas en < 5 ms crea una experiencia de usuario sólida y ultrarrápida.")

        CalloutBox {
            ArticleParagraph(
                "💡 Conclusión de Arquitectura: No emplees una red neuronal de 2.000 millones de parámetros para sumar 15 + 20 o comprobar si las 17:00 se solapan con las 17:30. Una arquitectura híbrida combina lo mejor de dos mundos: precisión determinista sub-5ms y fluidez conversacional en lenguaje natural."
            )
        }
    }
}
